import * as React from 'react';

import { Form } from '@ant-design/compatible';
import '@ant-design/compatible/assets/index.css';

import { Alert, Modal, Row } from 'antd';
import { connect } from 'react-redux';

import { FormComponentProps } from '@ant-design/compatible/lib/form/Form';
import { withRouter } from 'react-router';
import {
	compareStrings,
	nullSafeGet,
	nullSafeGetOrElse,
	stopDefaultEvents,
} from '../../../utils/DataAccessUtils';
import { getCalculatedChatHeight, parseMessageToElements } from '../../../utils/ChatUtils';
import moment from 'moment';
import { retrieveCachedUserDetails } from '../../../thunks/session_thunks';
import { ImageThumbnails } from '../../image_thumbnails/ImageThumbnails';
import { getFileMimeTypeFromString } from '../../../utils/FileAccessUtils';
import ChatInput from '../../ChatInput';
import { appendBidderNotes, fetchAllBidderNotes } from '../../../thunks/bidders_thunks';
import { receiveLoadingMessage, receiveNewMessage } from '../../../actions/messages_actions';
import { EmptyState } from '../../empty_state/EmptyState';
import MessageLog from '../../MessageLog';
import MessageBubble from '../../MessageBubble';
import { PageLoadingPlaceholder } from '../../page_loading_placeholder/PageLoadingPlaceholder';

const style = require('./BidderNotes.less');
const FormItem = Form.Item;

const getMessageChannelId = (selectedBidder) =>
	`${selectedBidder.requestForProposalId}_${selectedBidder.supplierFacilityId}`;

interface BidderNoteFormProps extends FormComponentProps {
	selectedBidder: any;
	appendBidderNotes: any;
	loading: boolean;
	currentUser: any;
	history: any;
	messages: any;
	startedTyping: any;
	receiveLoadingMessage: any;
	stoppedTyping: any;
	receiveMessage: any;
	getMeetingToken: any;
	owShortenedLink: any;
	successCallback?: any;
	markNotesSeen?: any;
	updateWOUnreadNotification?: any;
	fetchMessages?: any;
	redirectToUrl?: string;
	userType?: string;
	errors: string[];
}

class BidderNotesPage extends React.Component<BidderNoteFormProps, any> {
	state = {
		photoFileList: [],
		photoLinks: [],
		photoPreviewModalVisible: false,
		videoFileList: [],
		videoLink: '',
		videoPreviewModalVisible: false,
		connected: true,
		calculatedHeight: 0,
	};

	setTargetHeight = () => {
		this.setState({
			calculatedHeight: getCalculatedChatHeight('rfp-detail-header', ['bidder-chat-header']),
		});
	};

	sendMessage = (JSONString, retries = 3) => {
		const { appendBidderNotes, selectedBidder, receiveLoadingMessage } = this.props;

		const entity = JSON.parse(JSONString);

		if (nullSafeGet('noteType', entity) === 'heartbeat') {
			console.log('___ Ignoring append notes for failed sockets for hearbeat ___ ');
		} else {
			appendBidderNotes(selectedBidder, {
				note: entity,
				rfpId: nullSafeGet('requestForProposalId', selectedBidder),
			}).then((data) => {
				if (!entity.text) {
					receiveLoadingMessage(getMessageChannelId(selectedBidder), entity);
					const element = document.getElementById('chatBox');
					if (element) {
						element.scrollTop = element.scrollHeight;
					}
				}
			});
		}
	};

	componentDidMount() {
		const { selectedBidder, fetchMessages } = this.props;
		fetchMessages(selectedBidder);
		this.setTargetHeight();
		window.addEventListener('resize', this.setTargetHeight);
	}

	componentWillUnmount() {
		window.removeEventListener('resize', this.setTargetHeight);
	}

	componentDidUpdate(prevProps) {
		if (
			nullSafeGet('selectedBidder.supplierFacilityId', this.props) !==
			nullSafeGet('selectedBidder.supplierFacilityId', prevProps)
		) {
			this.props.fetchMessages(this.props.selectedBidder);
		}
		if (prevProps !== this.props) {
			this.setTargetHeight();
		}
	}

	handleSubmit = (e) => {
		stopDefaultEvents(e);
		this.props.form.validateFields((err, values) => {
			if (!err) {
				const { selectedBidder, receiveLoadingMessage, currentUser } = this.props;
				const messageText = values.noteText;

				const { elements, text } = parseMessageToElements(messageText);

				const entity = {
					text,
					noteAddedBy: currentUser.email,
					noteAddedByContact: currentUser,
					noteAddedAt: moment.utc(),
					elements,
				};
				receiveLoadingMessage(getMessageChannelId(selectedBidder), entity);
				setTimeout(() => {
					const element = document.getElementById('chatBox');
					if (element) {
						element.scrollTop = element.scrollHeight;
					}
				}, 50);
				this.sendMessage(JSON.stringify(entity));
				this.props.form.resetFields();
			}
		});
	};

	handlePhotoUploadChange = (info) => {
		this.setState({ photoFileList: info.fileList });
		if (info.fileList.some((file) => compareStrings(file.status, 'uploading'))) {
			this.setState({ photoUploadLoading: true });
			return;
		}
		if (info.fileList.every((file) => compareStrings(file.status, 'done'))) {
			const photoLinks = info.fileList.map((file) => nullSafeGetOrElse('response.data', file, ''));
			this.setState({
				photoUploadLoading: false,
				photoLinks: photoLinks || [],
				photoPreviewModalVisible: true,
			});
			return;
		}
	};

	handleVideoUploadChange = (info) => {
		this.setState({ videoFileList: info.fileList });
		if (info.file.status === 'uploading') {
			this.setState({ videoUploadLoading: true });
			return;
		}
		if (info.file.status === 'done') {
			const videoLink = nullSafeGetOrElse('file.response.data', info, '');
			this.setState({
				videoUploadLoading: false,
				videoLink: videoLink,
				videoPreviewModalVisible: true,
			});
		}
	};

	onPhotoSubmit = () => {
		const { currentUser } = this.props;
		const entities = this.state.photoLinks.map((photoLink) => ({
			text: '',
			photo: photoLink,
			noteAddedBy: currentUser.email,
			noteAddedByContact: currentUser,
			noteAddedAt: moment.utc(),
		}));
		entities.forEach((entity) => this.sendMessage(JSON.stringify(entity)));
		this.setState({ photoLinks: [], photoPreviewModalVisible: false, photoFileList: [] });
	};

	onPhotoCancel = () => {
		this.setState({ photoLinks: [], photoPreviewModalVisible: false, photoFileList: [] });
	};

	onVideoSubmit = () => {
		const { currentUser } = this.props;
		const entity = {
			text: '',
			video: this.state.videoLink,
			noteAddedBy: currentUser.email,
			noteAddedByContact: currentUser,
			noteAddedAt: moment.utc(),
		};
		this.sendMessage(JSON.stringify(entity));
		this.setState({ videoLink: '', videoPreviewModalVisible: false, videoFileList: [] });
	};

	onVideoCancel = () => {
		this.setState({ videoLink: '', videoPreviewModalVisible: false, videoFileList: [] });
	};

	render() {
		const { getFieldDecorator } = this.props.form;
		const { currentUser, errors, selectedBidder, messages, userType } = this.props;
		const token = retrieveCachedUserDetails(['token']).token;

		const id = getMessageChannelId(selectedBidder);
		const notes = nullSafeGetOrElse(`${id}.messages`, messages, []);
		const isLoading = nullSafeGetOrElse(`${id}.isLoading`, messages, false);
		const actualNotes = notes.filter(
			(n) =>
				(n.text && n.text.length > 0) ||
				(n.photo && n.photo.length > 0) ||
				(n.video && n.video.length > 0) ||
				n.changedBy
		);
		const multiplePhotos = nullSafeGetOrElse('photoLinks.length', this.state, 1) > 1;
		if (isLoading && actualNotes.length < 1) {
			return <PageLoadingPlaceholder />;
		}
		return (
			<div
				className="BidderNotes "
				style={{
					height: `${this.state.calculatedHeight}px`,
					maxHeight: `${this.state.calculatedHeight}px`,
				}}
			>
				<div
					id="chatBox"
					style={{
						flexGrow: 1,
						overflowY: 'auto',
					}}
					className="no-scrollbar"
				>
					{actualNotes.length > 0 ? (
						<div className="chatList">
							{actualNotes.map((n, idx) => {
								return n.type === 'log' ? (
									<MessageLog log={n} />
								) : (
									<MessageBubble
										currentUser={currentUser}
										n={n}
										idx={idx}
										userType={userType}
										key={idx}
									/>
								);
							})}
						</div>
					) : (
						<EmptyState
							graphic={
								<img
									style={{ marginBottom: 8 }}
									src="https://s3.amazonaws.com/mock-data-assets/categories/images/letter owl.svg"
									alt="Add a note to this bidder using the box below."
								/>
							}
							headline={'No notes have been added yet'}
							body={
								<div style={{ textAlign: 'center' }}>
									<div style={{ maxWidth: 440, marginBottom: 16 }}>
										Add a note to this bidder using the box below.
									</div>
								</div>
							}
						/>
					)}
				</div>
				<div>
					<Form layout="inline" onSubmit={this.handleSubmit}>
						{errors.length > 0
							? errors.map((error, idx) => (
									<FormItem key={idx}>
										<Alert message={error} type="error" />
									</FormItem>
							  ))
							: null}
						<Modal
							visible={this.state.photoPreviewModalVisible}
							width={600}
							title={multiplePhotos ? 'Send these photos?' : 'Send this photo?'}
							okText="Send"
							onCancel={this.onPhotoCancel}
							onOk={this.onPhotoSubmit}
							closable={false}
						>
							<Row style={{ gap: 12 }}>
								{this.state.photoLinks.map((photoLink) => (
									<ImageThumbnails imageWidth={100} imageHeight={100} photoStrings={[photoLink]} />
								))}
							</Row>
						</Modal>
						<Modal
							visible={this.state.videoPreviewModalVisible}
							width={600}
							title="Send this video?"
							okText="Send"
							onCancel={this.onVideoCancel}
							onOk={this.onVideoSubmit}
							closable={false}
						>
							<video width="100%" height="auto" controls>
								<source
									src={this.state.videoLink}
									type={getFileMimeTypeFromString(this.state.videoLink)}
								/>
								Your browser does not support the video tag.
							</video>
						</Modal>
						<div style={{ position: 'relative' }}>
							{getFieldDecorator('noteText', {
								initialValue: '',
								rules: [{ required: true, message: "Whoops! You can't send an empty note." }],
							})(
								<ChatInput
									handlePhotoUploadChange={this.handlePhotoUploadChange}
									workOrder={{ id: nullSafeGet('workOrderId', selectedBidder) }}
									handleVideoUploadChange={this.handleVideoUploadChange}
									handleSubmit={this.handleSubmit}
									currentUser={currentUser}
									token={token}
									photoFileList={this.state.photoFileList}
									videoFileList={this.state.videoFileList}
								/>
							)}
						</div>
						<div
							style={{
								height: 20,
								padding: '0 12px',
								marginBottom: 4,
								display: 'flex',
								justifyContent: 'space-between',
								color: 'rgba(0,0,0,0.45)',
								fontSize: 14,
							}}
						>
							<div>
								{this.props.form.getFieldValue('noteText') &&
								this.props.form.getFieldValue('noteText').length > 0 ? (
									<span>
										Press <span style={{ textTransform: 'uppercase' }}>Enter</span> to send
									</span>
								) : null}
							</div>
						</div>
					</Form>
				</div>
			</div>
		);
	}
}

const mapStateToProps = (state, ownProps) => ({
	history: ownProps.history,
	userType: state.session.userType,
	isAuthenticated: state.session.isAuthenticated,
	messages: state.messages,
	successCallback: ownProps.successCallback,
	redirectToUrl: ownProps.redirectToUrl,
	loading: state.work_orders.updating,
	currentUser: state.session.currentUser,
	errors: state.session.errors,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	appendBidderNotes: (bidder, note) => dispatch(appendBidderNotes(ownProps.userType)(bidder, note)),
	receiveMessage: (channelId, message) => dispatch(receiveNewMessage(channelId, message)),
	receiveLoadingMessage: (channelId, message) =>
		dispatch(receiveLoadingMessage(channelId, message)),
	fetchMessages: (bidder) => dispatch(fetchAllBidderNotes(ownProps.userType)(bidder)),
});

const ComponentWithoutUserType = withRouter(
	connect(mapStateToProps, mapDispatchToProps)(Form.create<BidderNoteFormProps>()(BidderNotesPage))
);

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
	}),
	() => ({})
)(ComponentWithoutUserType);
